/** @format */
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NG_ENTITY_SERVICE_CONFIG } from '@datorama/akita-ng-entity-service';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import { FullCalendarModule } from '@fullcalendar/angular';
import { NgxFudisModule, PaginationComponent } from '@funidata/ngx-fudis';
import { NgbActiveModal, NgbDropdownConfig } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleModule } from '@ng-idle/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { UIRouterUpgradeModule } from '@uirouter/angular-hybrid';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpBatchLink } from 'apollo-angular/http';
import { ClipboardModule } from 'ngx-clipboard';
import { LoggerModule, NGXLogger } from 'ngx-logger';
import {
    appAuthInitializer,
    authServiceProvider,
    defaultPromiseHandlerProvider,
    EnvironmentService,
    HIGHEST_LOGGING_LEVEL,
    httpInterceptorProviders,
    HybridInjectorService,
    IdleService,
    initialLanguageProvider,
    InjectorService,
    LocaleService,
    NO_SISU_ACCOUNT_STATE_NAME,
    NoSisuAccountRedirectService,
    setUpLogging,
    SisCommonModule,
    tokenStorageProvider,
    translationLoaderProvider,
    TranslocoCommonModule,
} from 'sis-common';
import {
    apolloFactory,
    AppErrorHandler,
    calendarEventServiceProvider,
    commonEducationServiceProvider,
    commonEnrolmentServiceProvider,
    commonStudyRightServiceProvider,
    commonTermRegistrationPeriodServiceProvider,
    ConnectionStatusService,
    courseUnitRealisationNameServiceProvider,
    courseUnitServiceProvider,
    curriculumPeriodServiceProvider,
    editModeServiceProvider,
    HeaderService,
    JsDataSyncService,
    legacyTranslateServiceProvider,
    LogService,
    NavUtilsService,
    PlanStructureModule,
    SessionTimeoutWarningService,
    SisComponentsModule,
    studyEventServiceProvider,
    transitionErrorHandler,
    validAttainmentFilterServiceProvider,
} from 'sis-components';
import { environment } from '../environments/environment';
import {
    courseUnitInfoModalProvider,
    enrolmentModalServiceProvider,
    studyProgressGraphDropDownContentFetcherProvider,
    uiContextProvider,
} from './ajs-upgraded-modules';
import { StudentAuthRouteModule } from './auth/student-auth-route.module';
import { StudentAuthRouteService } from './auth/student-auth-route.service';
import { studentRoutes } from './auth/student-routes';
import { CalendarModule } from './calendar/calendar.module';
import { StudentCommonModule } from './common/common.module';
import { StudentTranslateLoader } from './common/translate/student-translate-loader';
import { TranslocoStudentLoader } from './common/transloco/transloco-student-loader';
import { CourseCartModule } from './course-cart/course-cart.module';
import { EnrolmentsModule } from './enrolments/enrolments.module';
import { FrontpageModule } from './frontpage/frontpage.module';
import { LoginErrorModule } from './login-error/login-error.module';
import { MessageConversationsModule } from './message-conversations/message-conversations.module';
import { ModuleToggleOverridesModule } from './module-toggle-overrides/module-toggle-overrides.module';
import { OpenUniversityModule } from './open-university/open-university.module';
import { PlanNgModule } from './plan-ng/plan-ng.module';
import { PlanModule } from './plan/plan.module-ng';
import { ProfileModule } from './profile/profile.module-ng';
import { feedbackRoutes } from './routing/feedback.route';
import { SearchModule } from './search/search.module';
import { TimingModule } from './timing/timing.module';
import { TutoringModule } from './tutoring/tutoring.module-ng';

@NgModule({
    declarations: [],
    imports: [
        PlanNgModule,
        environment.production ? [] : AkitaNgDevtools.forRoot(),
        BrowserModule,
        BrowserAnimationsModule,
        CalendarModule,
        ClipboardModule,
        CourseCartModule,
        EnrolmentsModule,
        FrontpageModule,
        LoginErrorModule,
        LoggerModule.forRoot({
            level: HIGHEST_LOGGING_LEVEL,
        }),
        MessageConversationsModule,
        PlanModule,
        ProfileModule,
        SisComponentsModule,
        StudentCommonModule,
        TimingModule,
        TutoringModule,
        OpenUniversityModule,
        ModuleToggleOverridesModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: StudentTranslateLoader,
            },
        }),
        UIRouterUpgradeModule.forRoot({
            config: uiRouter => {
                // Override the default error handler that prints errors to console, handle errors in transition hook
                uiRouter.stateService.defaultErrorHandler(() => {});
                uiRouter.transitionService.onError({}, transitionErrorHandler(studentRoutes.defaultView));
            },
            states: feedbackRoutes,
        }),
        SisCommonModule,
        NgIdleModule.forRoot(),
        SearchModule,
        TranslocoCommonModule.forRoot({
            loader: TranslocoStudentLoader,
        }),
        ReactiveFormsModule,
        FormsModule,
        FullCalendarModule,
        NgxFudisModule,
        StudentAuthRouteModule,
        PlanStructureModule,
        PaginationComponent,
    ],
    providers: [
        NgbActiveModal,
        uiContextProvider,
        authServiceProvider,
        commonEducationServiceProvider,
        commonEnrolmentServiceProvider,
        commonStudyRightServiceProvider,
        commonTermRegistrationPeriodServiceProvider,
        courseUnitServiceProvider,
        courseUnitInfoModalProvider,
        curriculumPeriodServiceProvider,
        defaultPromiseHandlerProvider,
        httpInterceptorProviders,
        editModeServiceProvider,
        studyProgressGraphDropDownContentFetcherProvider,
        translationLoaderProvider,
        tokenStorageProvider,
        enrolmentModalServiceProvider,
        studyEventServiceProvider,
        validAttainmentFilterServiceProvider,
        { provide: LOCALE_ID, useValue: 'fi' },
        { provide: ErrorHandler, useExisting: AppErrorHandler },
        { provide: NO_SISU_ACCOUNT_STATE_NAME, useValue: studentRoutes.noSisuAccount },
        {
            provide: NG_ENTITY_SERVICE_CONFIG,
            useValue: {},
        },
        initialLanguageProvider,
        calendarEventServiceProvider,
        appAuthInitializer,
        courseUnitRealisationNameServiceProvider,
        Apollo,
        {
            provide: APOLLO_OPTIONS,
            useFactory: apolloFactory,
            deps: [HttpBatchLink],
        },
        JsDataSyncService,
        EnvironmentService.forRoot(environment),
        { provide: InjectorService, useClass: HybridInjectorService },
        legacyTranslateServiceProvider,
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {
    constructor(
        private logger: NGXLogger,
        private ngbDropdownConfig: NgbDropdownConfig,
        private localeService: LocaleService,
        private idleService: IdleService,
        private sessionTimeoutWarningService: SessionTimeoutWarningService,
        private connectionStatusService: ConnectionStatusService,
        private jsDataSyncService: JsDataSyncService,
        private studentLoginRouteService: StudentAuthRouteService,
        private logService: LogService,
        // Eager-injected so its errorResponse$ subscription is active before APP_INITIALIZER's
        // preauth resolves. Do not remove — without this the no-sisu-account flow silently no-ops.
        private noSisuAccountRedirectService: NoSisuAccountRedirectService,
        headerService: HeaderService,
        navUtilsService: NavUtilsService,
    ) {
        this.logService.init('/osuva/jsnlog.logger');
        setUpLogging(this.logger);
        this.ngbDropdownConfig.autoClose = 'outside';
        headerService.init();
        navUtilsService.init();
    }

    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method,@angular-eslint/use-lifecycle-interface
    ngDoBootstrap() {}
}
